import debounce from 'lodash/debounce'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { v4 as uuidV4 } from 'uuid'
import { MessageKeys, useMessage } from '@opallabs/hooks'
import {
  useRegisteredTiptapEditors,
  type RegisteredTiptapEditor,
} from '@opallabs/text-editor'
import { buildTextEditorDocuments } from './textEditorContext'
import {
  handleTiptapEditResult,
  resolveEditTarget,
  restageTiptapEdit,
} from './toolCallResultHandlers'
import { type TextEditorDocument } from './types'

/**
 * Bridges iframe content text editors to the Gem transport in the parent window.
 * The parent registry can't see editors mounted in the iframe, so this component
 * (mounted alongside them) pushes their context up for `set_text_editor_context`
 * and applies the `tiptap_edit` results the parent forwards back down.
 */
export const GemContentEditorBridge = () => {
  const registeredEditors = useRegisteredTiptapEditors()
  // Every post on a legacy page mounts its own bridge, so the parent keeps
  // each bridge's documents apart by this id. See `updateBridgeDocuments`.
  const [bridgeId] = useState(() => uuidV4())

  const publishDocuments = useCallback(
    (documents: TextEditorDocument[]) => {
      window.top?.postMessage(
        {
          message: MessageKeys.setTextEditorContext,
          bridge_id: bridgeId,
          documents,
        },
        '*'
      )
    },
    [bridgeId]
  )

  const publishContext = useMemo(
    () =>
      debounce((editors: RegisteredTiptapEditor[]) => {
        publishDocuments(buildTextEditorDocuments(editors))
      }, 150),
    [publishDocuments]
  )

  useEffect(() => {
    publishContext(registeredEditors)

    const handleChange = () => publishContext(registeredEditors)
    registeredEditors.forEach(({ editor }) => {
      editor.on('update', handleChange)
      editor.on('selectionUpdate', handleChange)
      // Switching a document between editing and view-only changes no content
      // and moves no cursor, so only a transaction reports it. Until it did,
      // the agent kept refusing an edit the user had just enabled.
      editor.on('transaction', handleChange)
    })

    return () => {
      registeredEditors.forEach(({ editor }) => {
        editor.off('update', handleChange)
        editor.off('selectionUpdate', handleChange)
        editor.off('transaction', handleChange)
      })
    }
  }, [registeredEditors, publishContext])

  // The editors are going away, so the parent must hear that before anything
  // else: a pending debounce would otherwise deliver the document it is
  // losing, and the parent would keep re-sending it on every later message.
  useEffect(
    () => () => {
      publishContext.cancel()
      publishDocuments([])
    },
    [publishContext, publishDocuments]
  )

  // Reloading the iframe unmounts nothing, so without this the parent would
  // keep merging the documents of a page that is gone into every message.
  useEffect(() => {
    const handlePageHide = () => {
      publishContext.cancel()
      publishDocuments([])
    }
    window.addEventListener('pagehide', handlePageHide)
    return () => window.removeEventListener('pagehide', handlePageHide)
  }, [publishContext, publishDocuments])

  useMessage(MessageKeys.tiptapEditResult, (event) => {
    const { result, restage } = event.data
    // A soft error names no document and stages nothing.
    if (!('operations' in result)) return
    if (restage) {
      // Only the document the edit was written for, never the lone-editor
      // fallback: every bridge on the page hears this.
      const target = registeredEditors.find(
        (entry) => entry.documentId === result.document_id
      )
      if (target) restageTiptapEdit(target, result)
      return
    }
    const target = resolveEditTarget(registeredEditors, result.document_id)
    if (target) handleTiptapEditResult(target, result)
  })

  return null
}
